'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { Icon, type IconName } from './icon'
import { toast } from 'sonner'
import { CartaoPreview } from './cartao-preview'
import type { CartaoPayload } from '@/lib/export/types'

type Publico = 'cliente' | 'eu'
type Formato = 'png' | 'pdf'

interface ExportModalProps {
  open: boolean
  onClose: () => void
  /** Mostra o seletor pro-cliente/minha-cópia (só na precificação). */
  comPublico?: boolean
  /** Constrói o payload do cartão pro público escolhido (null = cartão único). */
  buildCartao: (publico: Publico | null) => CartaoPayload
}

export function ExportModal({ open, onClose, comPublico = false, buildCartao }: ExportModalProps) {
  const [audience, setAudience] = useState<Publico>('cliente')
  const [formato, setFormato] = useState<Formato>('png')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  const publico = comPublico ? audience : null
  const payload = buildCartao(publico)
  const paraCliente = publico === 'cliente'

  function baixar(blob: Blob, ext: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `calculamei-${payload.nome}.${ext}`
    // iOS Safari ignora `download` em blob: target _blank abre o arquivo numa aba
    // pra a pessoa ver e salvar. No desktop/Android, baixa direto.
    a.target = '_blank'
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 15000)
  }

  async function onSalvar() {
    try {
      setSaving(true)
      const res = await fetch(`/api/export/${formato}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        toast.error('Não consegui gerar o arquivo. Tenta de novo em instantes.')
        return
      }
      baixar(await res.blob(), formato)
    } catch {
      toast.error('Sem conexão pra gerar o arquivo. Verifica a internet e tenta de novo.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Salvar resultado"
    >
      <button type="button" aria-label="Fechar" onClick={onClose} className="absolute inset-0 bg-[#0B1517]/65" />

      <div className="relative grid max-h-[92vh] w-full max-w-[760px] grid-cols-1 overflow-auto rounded-2xl border border-line bg-surface shadow-lift md:grid-cols-2">
        {/* ── CONTROLES ── */}
        <div className="border-b border-line p-[22px] md:border-b-0 md:border-r">
          <div className="mb-5 flex items-start justify-between">
            <div>
              <div className="text-[17px] font-semibold tracking-[-0.01em] text-ink">Salvar resultado</div>
              <div className="mt-0.5 text-[12.5px] text-ink-mute">Confira a prévia, escolha o formato e salve.</div>
            </div>
            <button type="button" aria-label="Fechar" onClick={onClose} className="-mr-1 p-1 text-ink-mute hover:text-ink">
              <Icon name="x" size={22} />
            </button>
          </div>

          {comPublico && (
            <>
              <div className="mb-1 text-[13.5px] font-medium text-ink">Pra quem é esse resultado?</div>
              <p className="mb-3 text-xs leading-relaxed text-ink-mute">
                Isso decide se o seu lucro aparece. No padrão, ele fica escondido.
              </p>
              <RadioPublico ativo={paraCliente} onClick={() => setAudience('cliente')} icon="send" titulo="Mandar pro cliente" badge="padrão" sub="Esconde o seu lucro" subIcon="eye-off" />
              <RadioPublico ativo={!paraCliente} onClick={() => setAudience('eu')} icon="user" titulo="É minha cópia" sub="Mostra o seu lucro · só pra você" />
              {paraCliente && (
                <div className="mb-[18px] flex items-start gap-2.5 rounded-[10px] border border-brand-tint-strong bg-brand-tint p-3">
                  <Icon name="check" size={15} className="mt-0.5 text-brand" />
                  <p className="text-xs leading-relaxed text-brand-dark">
                    Seguro pra enviar. O cliente vê o preço e o que está incluído, mas não quanto você ganha.
                  </p>
                </div>
              )}
            </>
          )}

          {/* seletor de formato (só escolha — não baixa) */}
          <div className="mb-2.5 text-[13.5px] font-medium text-ink">Em qual formato?</div>
          <div className="mb-4 grid grid-cols-2 gap-2.5">
            <FormatoTile ativo={formato === 'png'} onClick={() => setFormato('png')} icon="image" label="Imagem" sub="PNG · pro WhatsApp" />
            <FormatoTile ativo={formato === 'pdf'} onClick={() => setFormato('pdf')} icon="file" label="PDF" sub="documento formal" />
          </div>

          {/* botão de salvar (gera o formato selecionado) */}
          <button
            type="button"
            onClick={onSalvar}
            disabled={saving}
            className="flex h-[50px] w-full items-center justify-center gap-2.5 rounded-[10px] bg-brand font-medium text-white shadow-[0_6px_16px_rgb(14_92_99/0.20)] transition-colors hover:bg-brand-dark disabled:opacity-60"
          >
            <Icon name="download" size={18} />
            {saving ? 'Salvando…' : `Salvar ${formato === 'png' ? 'imagem' : 'PDF'}`}
          </button>

          <p className="mt-3 text-center text-[11.5px] text-ink-faint">
            Grátis e sem limite. Sai com o rodapé do CalculaMEI.
          </p>
        </div>

        {/* ── PRÉVIA ── */}
        <div className="bg-bg-alt p-[22px]">
          <div className="mb-3 text-[13px] font-medium text-ink-mute">Prévia — é assim que vai sair</div>
          <CartaoPreview payload={payload} />
          {comPublico && (
            <div className="mt-3.5 flex items-start gap-2 text-[11.5px] leading-relaxed text-ink-mute">
              <Icon name={paraCliente ? 'eye-off' : 'user'} size={14} className="mt-0.5" />
              <span>
                {paraCliente
                  ? 'Sem o seu lucro. A linha vira “Total” — pronto pra enviar ao cliente.'
                  : 'Com o seu lucro à mostra. Guarde pra você, não envie ao cliente.'}
              </span>
            </div>
          )}
          {formato === 'pdf' && (
            <p className="mt-3.5 text-[11.5px] leading-relaxed text-ink-faint">
              O PDF sai como um documento formal (cabeçalho, data e detalhamento) — ideal pra
              anexar ou imprimir. A prévia acima é o cartão; o PDF é a versão documento.
            </p>
          )}
        </div>
      </div>

    </div>
  )
}

function FormatoTile({
  ativo,
  onClick,
  icon,
  label,
  sub,
}: {
  ativo: boolean
  onClick: () => void
  icon: IconName
  label: string
  sub: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className={cn(
        'flex items-start gap-2.5 rounded-[12px] border p-3 text-left transition-colors',
        ativo ? 'border-2 border-brand bg-brand-tint' : 'border border-line bg-surface hover:border-brand'
      )}
    >
      <Icon name={icon} size={20} className={cn('mt-0.5', ativo ? 'text-brand' : 'text-ink-mute')} />
      <span className="min-w-0">
        <span className={cn('block text-sm font-semibold', ativo ? 'text-brand' : 'text-ink')}>{label}</span>
        <span className="block text-[11px] text-ink-mute">{sub}</span>
      </span>
      {ativo && <Icon name="check" size={16} strokeWidth={2.2} className="ml-auto text-brand" />}
    </button>
  )
}

function RadioPublico({
  ativo,
  onClick,
  icon,
  titulo,
  badge,
  sub,
  subIcon,
}: {
  ativo: boolean
  onClick: () => void
  icon: IconName
  titulo: string
  badge?: string
  sub: string
  subIcon?: IconName
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'mb-2.5 flex w-full items-start gap-3 rounded-[12px] p-3.5 text-left transition-colors',
        ativo ? 'border-2 border-brand bg-brand-tint' : 'border border-line bg-surface hover:border-brand'
      )}
    >
      <Icon name={icon} size={19} className={cn('mt-0.5', ativo ? 'text-brand' : 'text-ink-mute')} />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className={cn('text-sm font-medium', ativo ? 'text-brand' : 'text-ink')}>{titulo}</span>
          {badge && (
            <span className="rounded-full bg-brand-tint-strong px-2 py-px font-mono text-[10px] tracking-[0.04em] text-brand">
              {badge}
            </span>
          )}
        </div>
        <div className={cn('mt-0.5 flex items-center gap-1.5 text-xs', ativo ? 'text-brand-dark' : 'text-ink-mute')}>
          {subIcon && <Icon name={subIcon} size={13} />}
          {sub}
        </div>
      </div>
      {ativo && <Icon name="check" size={18} strokeWidth={2.2} className="text-brand" />}
    </button>
  )
}
